// components/capture/RegionCard.jsx — Anamoria SPA
// v1.0 — 2.2b: AD card-shape wrapper for capture regions (July 29, 2026)
//
// Presentational only. Frames a capture region as the AD "card": a collapsed
// entry point (icon + serif label + helper) that expands in place to reveal the
// region's existing controls. It changes ONLY the idle presentation — the
// region's functionality (chips/pieces, 10-piece cap, add/remove, the recorder
// with its own MicIcon/CamIcon) is unchanged and rendered as children.
//
// The caller keeps its <fieldset> + hidden <legend> for grouping (AD §1.16
// a11y); this renders the visible head (a real <button>, aria-expanded) and the
// body. `open` is caller-derived (expanded || hasContent) so a card with pieces
// stays open. When open, the helper line steps aside.
//
// Props:
//   icon     — node — card-header icon (the region's own; e.g. RecordingIcon)
//   title    — string — serif card label ("Write", "A photo", …)
//   helper   — string — one-line helper, shown only while collapsed
//   open     — boolean — expanded or has content
//   disabled — boolean — at cap + empty (mirrors the region's atCap gate)
//   onToggle — function — expand/collapse (or open picker, per region)
//   children — node — the region's existing controls (rendered when open)

import styles from '../../pages/AddMemoryPage.module.css';

export default function RegionCard({ icon, title, helper, open = false, disabled = false, onToggle, children }) {
  return (
    <div className={`${styles.regionCard} ${open ? styles.regionCardOpen : ''} ${disabled ? styles.regionCardDisabled : ''}`}>
      <button
        type="button"
        className={styles.regionCardHead}
        onClick={onToggle}
        disabled={disabled}
        aria-expanded={open}
      >
        <span className={styles.regionCardIcon} aria-hidden="true">{icon}</span>
        <span className={styles.regionCardTextWrap}>
          <span className={styles.regionCardTitle}>{title}</span>
          {!open && <span className={styles.regionCardHelper}>{helper}</span>}
        </span>
      </button>
      {open && <div className={styles.regionCardBody}>{children}</div>}
    </div>
  );
}

/* Card-header icons (AD). The recorder's own MicIcon/CamIcon are unchanged and
   live in RecordingRegion; these are only the collapsed card glyphs. */

export function WriteIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 20 L4 16 L15 5 a2 2 0 0 1 3 3 L7 19 Z" />
      <path d="M4 20 h4" />
    </svg>
  );
}

export function PhotoIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="8.5" cy="10" r="1.5" />
      <path d="M21 16 l-5-5 -8 8" />
    </svg>
  );
}

export function RecordingIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
      <path d="M4 10v4M8 6v12M12 3v18M16 7v10M20 10v4" />
    </svg>
  );
}

export function DayIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 9h18M8 3v4M16 3v4" />
    </svg>
  );
}
